import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import {
  DocIcon,
  docKindFor,
  displayFileName,
} from "../../../components/ui/icons";
import { formatSizeInMB, formatDate } from "../../../utils/formatUtils";
import type { UserDocumentResponse } from "../../../services";

interface MultipleFilesSectionProps {
  multipleFiles: File[];
  multipleSelectedDocuments: UserDocumentResponse[];
  onFileRemove: (index: number) => void;
  onDocumentDeselect: (documentId: string) => void;
  onFileUpload: (files: FileList) => void;
  onClearAll: () => void;
  onDownloadDocument: (document: UserDocumentResponse) => void;
  isProcessing: boolean;
  isSmallScreen: boolean;
  maxFiles?: number;
  /** Render file/document text for a light background (default false = legacy dark panel). */
  onLightBackground?: boolean;
}

const MultipleFilesSection: React.FC<MultipleFilesSectionProps> = ({
  multipleFiles,
  multipleSelectedDocuments,
  onFileRemove,
  onDocumentDeselect,
  onFileUpload,
  onClearAll,
  onDownloadDocument,
  isProcessing,
  isSmallScreen,
  maxFiles = 5,
  onLightBackground = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const totalDocuments = multipleFiles.length + multipleSelectedDocuments.length;

  // The legacy panel is dark (white text); a light host needs dark text.
  const lightText = isSmallScreen || onLightBackground;
  const primaryTextColor = lightText
    ? theme.palette.text.primary
    : theme.palette.common.white;
  const secondaryTextColor = lightText
    ? theme.palette.text.secondary
    : alpha(theme.palette.common.white, 0.7);
  // Supporting copy on the dark panel: never text.disabled / mid-teal — those
  // fall below 4.5:1 on navy (UI/UX audit 2.4.1).
  const hintTextColor = lightText
    ? theme.palette.text.secondary
    : alpha(theme.palette.common.white, 0.6);
  const successColor = lightText
    ? theme.palette.status.successFg
    : theme.palette.brand.cyanHead;
  const chipSx = {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontSize: "11px",
    color: lightText
      ? theme.palette.text.primary
      : theme.palette.brand.cyanHead,
    borderColor: lightText
      ? theme.palette.primary.main
      : theme.palette.brand.cyanHead,
  } as const;


  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing && totalDocuments < maxFiles) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing && totalDocuments < maxFiles) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget;
    
    if (!currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!isProcessing && e.dataTransfer.files.length > 0 && totalDocuments < maxFiles) {
      const files = Array.from(e.dataTransfer.files);
      
      // Validate file types
      const validTypes = [".pdf", ".docx"];
      const invalidFiles = files.filter((file) => {
        const fileName = file.name.toLowerCase();
        return !validTypes.some((type) => fileName.endsWith(type));
      });

      if (invalidFiles.length > 0) {
        // You might want to show an error here or pass it back to parent
        console.error(
          `Alleen PDF en Word documenten zijn toegestaan. Ongeldige bestanden: ${invalidFiles.map((f) => f.name).join(", ")}`
        );
        return;
      }

      // Validate total file count
      if (totalDocuments + files.length > maxFiles) {
        console.error(
          `Je kunt maximaal ${maxFiles} documenten selecteren. Verwijder eerst enkele documenten.`
        );
        return;
      }

      // Validate file sizes (20MB limit)
      const maxSizeInMB = 20;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      const oversizedFiles = files.filter((file) => file.size > maxSizeInBytes);

      if (oversizedFiles.length > 0) {
        console.error(
          `De volgende bestanden zijn te groot: ${oversizedFiles.map((f) => f.name).join(", ")}. Maximum toegestane grootte is ${maxSizeInMB}MB.`
        );
        return;
      }

      // Create a FileList to pass to the parent handler
      const dataTransfer = new DataTransfer();
      files.forEach(file => dataTransfer.items.add(file));
      
      // Call the parent upload handler
      onFileUpload(dataTransfer.files);
    }
  };

  return (
    <>
      {/* File Upload Area with Drag and Drop */}
      <Box mb={2}>
        <Box
          border={`2px dashed ${
            isDragOver
              ? alpha(theme.palette.primary.main, 0.7)
              : alpha(theme.palette.grey[500], 0.3)
          }`}
          borderRadius={2}
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          bgcolor={
            isDragOver
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.grey[500], 0.08)
          }
          sx={{
            cursor: totalDocuments >= maxFiles || isProcessing ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            opacity: totalDocuments >= maxFiles || isProcessing ? 0.5 : 1,
            pointerEvents: totalDocuments >= maxFiles || isProcessing ? "none" : "auto",
            "&:hover": totalDocuments < maxFiles && !isProcessing
              ? {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }
              : {},
          }}
        >
          <input
            type="file"
            hidden
            multiple
            id="multiple-file-upload-input"
            accept=".pdf,.docx"
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                onFileUpload(files);
              }
              e.target.value = "";
            }}
            disabled={isProcessing || totalDocuments >= maxFiles}
          />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
            onClick={() => {
              if (totalDocuments < maxFiles && !isProcessing) {
                document.getElementById("multiple-file-upload-input")?.click();
              }
            }}
            sx={{
              cursor: totalDocuments >= maxFiles || isProcessing ? "not-allowed" : "pointer",
            }}
          >
            <Icon
              icon={isDragOver ? "eva:cloud-upload-fill" : "eva:cloud-upload-outline"}
              style={{
                fontSize: 40,
                color: isDragOver
                  ? theme.palette.primary.main
                  : hintTextColor,
              }}
            />
            <Typography
              variant="body1"
              color={isDragOver ? theme.palette.primary.main : hintTextColor}
              align="center"
              sx={{ fontWeight: 500 }}
            >
              {isDragOver
                ? t("functionalities.files.dropHere")
                : totalDocuments >= maxFiles
                  ? t("functionalities.files.maxReached", { max: maxFiles })
                  : t("functionalities.files.clickOrDrag")}
            </Typography>
            <Typography
              variant="caption"
              color={hintTextColor}
              align="center"
            >
              {isDragOver
                ? t("functionalities.files.releaseToUpload")
                : t("functionalities.files.supportedFilesHint")}
              <br />
              {t("functionalities.files.maxFilesHint", { max: maxFiles })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Display Selected Files */}
      {multipleFiles.length > 0 && (
        <Box mb={2}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: primaryTextColor,
              fontWeight: 500,
            }}
          >
            {t("functionalities.files.uploadedFilesCount", {
              count: multipleFiles.length,
            })}
          </Typography>
          {multipleFiles.map((file, index) => (
            <Box
              key={`file-${index}`}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                mb: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                opacity: isProcessing ? 0.7 : 1,
                pointerEvents: isProcessing ? "none" : "auto",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <DocIcon kind={docKindFor(file.name)} size={32} />
                <Box>
                  <Typography variant="body2" color={primaryTextColor}>
                    {displayFileName(file.name)}
                  </Typography>
                  <Typography variant="caption" color={secondaryTextColor}>
                    {(file.size / (1024 * 1024)).toFixed(1)} MB •{" "}
                    {formatDate(new Date())}
                  </Typography>
                </Box>
              </Box>
              <Tooltip title={t("functionalities.files.removeFile")}>
                <span>
                  <IconButton
                    size="Xs"
                    onClick={() => onFileRemove(index)}
                    sx={{
                      flexShrink: 0,
                      color: lightText
                        ? theme.palette.error.main
                        : theme.palette.error.light,
                    }}
                    disabled={isProcessing}
                    aria-label={t("functionalities.files.removeFile")}
                    title={t("functionalities.files.removeFile")}
                  >
                    <Icon icon="mingcute:close-line" fontSize={20} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Display Selected Documents */}
      {multipleSelectedDocuments.length > 0 && (
        <Box mb={2}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: primaryTextColor,
              fontWeight: 500,
            }}
          >
            {t("functionalities.files.selectedDocumentsCount", {
              count: multipleSelectedDocuments.length,
            })}
          </Typography>
          {multipleSelectedDocuments.map((document) => (
            <Box
              key={`doc-${document.id}`}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                mb: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                opacity: isProcessing ? 0.7 : 1,
                pointerEvents: isProcessing ? "none" : "auto",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <DocIcon
                  kind={docKindFor(document.tittle || document.documentLink)}
                  size={32}
                />
                <Box>
                  <Typography variant="body2" color={primaryTextColor}>
                    {displayFileName(document.tittle)}
                  </Typography>
                  <Typography variant="caption" color={secondaryTextColor}>
                    {/* documentSize is a raw byte count from the API. */}
                    {formatSizeInMB(document.documentSize)} •{" "}
                    {document.createdDate
                      ? formatDate(document.createdDate)
                      : t("common.unknownDate")}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1}>
                <Tooltip title={t("functionalities.files.downloadDocument")}>
                  <span>
                    <IconButton
                      size="Xs"
                      onClick={() => onDownloadDocument(document)}
                      aria-label={t("functionalities.files.downloadDocument")}
                      title={t("functionalities.files.downloadDocument")}
                      sx={{ flexShrink: 0 }}
                      disabled={isProcessing}
                    >
                      <Icon icon="mingcute:download-line" fontSize={20} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("functionalities.files.deselect")}>
                  <span>
                    <IconButton
                      size="Xs"
                      onClick={() => onDocumentDeselect(document.id)}
                      sx={{
                        flexShrink: 0,
                        color: lightText
                          ? theme.palette.error.main
                          : theme.palette.error.light,
                      }}
                      disabled={isProcessing}
                      aria-label={t("functionalities.files.deselect")}
                      title={t("functionalities.files.deselect")}
                    >
                      <Icon icon="mingcute:close-line" fontSize={20} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Clear All Button */}
      {totalDocuments > 0 && (
        <Box mb={2}>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={onClearAll}
            disabled={isProcessing}
            startIcon={<Icon icon="mingcute:delete-line" />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {t("functionalities.files.clearAll")}
          </Button>
        </Box>
      )}

      {/* Selection Status */}
      {totalDocuments > 0 && (
        <Box
          mt={2}
          p={2}
          bgcolor={alpha(theme.palette.success.main, 0.1)}
          borderRadius={2}
          border={`1px solid ${alpha(theme.palette.success.main, 0.2)}`}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Icon
              icon="mingcute:check-circle-fill"
              style={{ fontSize: 16, color: successColor }}
            />
            <Typography
              variant="body2"
              color={successColor}
              sx={{ fontWeight: 500 }}
            >
              {t("functionalities.files.documentsSelectedCount", {
                count: totalDocuments,
              })}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {multipleFiles.map((file, index) => (
              <Chip
                key={`chip-file-${index}`}
                label={displayFileName(file.name)}
                size="small"
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MultipleFilesSection;